import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../axios';
import { Permission, PermissionData } from '../types/permissionTypes';
import { refreshDesignations } from './refreshDesignations';

// Utility function to log and ensure boolean values
const ensureBooleanValues = (data: any) => {
  if (data && data.permissions) {
    // Create a deep copy to avoid mutation issues
    const processedData = {
      ...data,
      permissions: { ...data.permissions },
    };

    // Specifically ensure Role permissions are properly set as boolean
    if ('roleRead' in processedData.permissions) {
      processedData.permissions.roleRead = Boolean(processedData.permissions.roleRead);
    }
    if ('roleWrite' in processedData.permissions) {
      processedData.permissions.roleWrite = Boolean(processedData.permissions.roleWrite);
    }
    if ('roleEdit' in processedData.permissions) {
      processedData.permissions.roleEdit = Boolean(processedData.permissions.roleEdit);
    }

    // Process all other permission fields too
    Object.keys(processedData.permissions).forEach(key => {
      if (typeof processedData.permissions[key] !== 'boolean' && key !== 'id') {
        processedData.permissions[key] = Boolean(processedData.permissions[key]);
      }
    });

    console.log('Processed permission data before API call:', processedData);
    return processedData;
  }

  return data;
};

// Get all permissions/designations
export const fetchPermissions = createAsyncThunk(
  'permissions/fetchAll',
  async ({ page = 1, limit = 10 }: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/v1/admin/roles?page=${page}&limit=${limit}`);
      console.log('API Response:', response.data); // Debug log to verify API response structure
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch permissions');
    }
  }
);

// Search permissions/designations by name (supports partial matches)
export const searchPermissions = createAsyncThunk(
  'permissions/search',
  async (name: string, { rejectWithValue, getState }) => {
    try {
      // To support partial matches, we fetch all permissions and filter on the client side
      const response = await axiosInstance.get(`/v1/admin/roles?limit=100`);

      if (response.data?.data) {
        // Filter permissions that have names containing the search term (case insensitive)
        const filteredData = response.data.data.filter((permission: Permission) =>
          permission.name.toLowerCase().includes(name.toLowerCase())
        );

        return {
          success: true,
          data: filteredData,
        };
      }

      return {
        success: true,
        data: [],
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search permissions');
    }
  }
);

// Get single permission/designation by ID
export const fetchPermissionById = createAsyncThunk(
  'permissions/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/v1/admin/role/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch permission');
    }
  }
);

interface CreatePermissionPayload {
  name: string;
  permissions: PermissionData;
}

// Create new permission/designation
export const createPermission = createAsyncThunk(
  'permissions/create',
  async (data: CreatePermissionPayload, { rejectWithValue, dispatch }) => {
    try {
      // Ensure all permission values are proper booleans before sending to API
      const processedData = ensureBooleanValues(data);

      // Log the final data being sent
      console.log(
        '[API Request] Creating permission with data:',
        JSON.stringify(processedData, null, 2)
      );

      const response = await axiosInstance.post('/v1/admin/create/role', processedData);
      
      // Immediately refresh designations after creating a new one
      try {
        dispatch(refreshDesignations());
        console.log('Designations refreshed after creating new permission/designation');
      } catch (refreshError) {
        console.error('Failed to refresh designations:', refreshError);
      }
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create permission');
    }
  }
);

// Update permission/designation
export const updatePermission = createAsyncThunk(
  'permissions/update',
  async ({ id, data }: { id: string; data: CreatePermissionPayload }, { rejectWithValue, dispatch }) => {
    try {
      // Ensure all permission values are proper booleans before sending to API
      const processedData = ensureBooleanValues(data);

      // Log the final data being sent
      console.log(
        '[API Request] Updating permission with data:',
        JSON.stringify(processedData, null, 2)
      );

      const response = await axiosInstance.put(`/v1/admin/role/${id}`, processedData);
      
      // Immediately refresh designations after updating
      try {
        dispatch(refreshDesignations());
        console.log('Designations refreshed after updating permission/designation');
      } catch (refreshError) {
        console.error('Failed to refresh designations:', refreshError);
      }
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update permission');
    }
  }
);
